import React from "react";
import PropTypes from "prop-types";
import NavigationComponent from "../../commonComponents/util/NavigationComponent";
import { formatDate } from "../../../lib/utils";
import PledgeRowDisplay from "./PledgeRowDisplay";
import PledgeRowEdit from "./PledgeRowEdit";
import PledgeTableHeader from "./PledgeTableHeader";

// Constants
const NO_PLEDGE_DISPLAY = "-";
const INITIAL_FORM_STATE = {
  pledgeUpdate: {
    pledgeDate: "",
    pledgeAmount: "",
    payOutSchedule: ""
  },
  editPledgeIndex: null,
  newPledge: false,
  submitAttempted: false
};

export default class ApplicantPledgesTable extends NavigationComponent {
  displayLocalDate = dateString => {
    if (!dateString) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-").map(Number);
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    return formatDate(dateString);
  };

  toNumber = value =>
    value === "" || value === null || value === undefined ? 0 : Number(value) || 0;

  constructor(props) {
    super(props);
    this.state = {
      showModals: {},
      ...INITIAL_FORM_STATE
    };
  }

  resetForm = () => this.setState(INITIAL_FORM_STATE);

  startEditPledge = (index, pledge) => {
    this.setState({
      editPledgeIndex: index,
      pledgeUpdate: {
        pledgeDate: pledge.pledgeDate || "",
        pledgeAmount: pledge.pledgeAmount || "",
        payOutSchedule: pledge.payOutSchedule || ""
      },
      submitAttempted: false
    });
  };

  addNewPledge = () => {
    this.setState({
      newPledge: true,
      pledgeUpdate: { ...INITIAL_FORM_STATE.pledgeUpdate },
      submitAttempted: false
    });
  };

  updateFormField = (fieldName, fieldValue) => {
    this.setState({
      pledgeUpdate: {
        ...this.state.pledgeUpdate,
        [fieldName]: fieldValue
      }
    });
  };

  disableSubmit = () => {
    const { pledgeUpdate } = this.state;
    return !pledgeUpdate.pledgeDate || !pledgeUpdate.pledgeAmount;
  };

  submitPledge = (isNewPledge = false) => {
    this.setState({ submitAttempted: true });
    if (this.disableSubmit()) return;

    const pledges = [...this.props.applicantPledges];
    if (isNewPledge) {
      pledges.push({ ...this.state.pledgeUpdate, amountPaid: 0 });
    } else {
      const existing = pledges[this.state.editPledgeIndex];
      pledges[this.state.editPledgeIndex] = {
        ...existing,
        ...this.state.pledgeUpdate,
        amountPaid: existing.amountPaid || 0
      };
    }

    this.props.updateRecipient("applicantPledges", pledges);
    this.resetForm();
  };

  render() {
    const { className, applicantPledges = [] } = this.props;
    const { pledgeUpdate, newPledge, editPledgeIndex } = this.state;
    const addingOrEditing = newPledge || editPledgeIndex !== null;
    const disableSubmit = this.disableSubmit();

    return (
      <div>
        <div className="full-width bank-accounts-edit pledges-edit" style={{ marginTop: "18px" }}>
          <label className={className}>Pledges </label>
          <span
            className={`link-text ${addingOrEditing ? "disabled" : ""}`}
            style={{ marginLeft: "8px", padding: 0, position: "relative", zIndex: 10 }}
            onClick={!addingOrEditing ? this.addNewPledge : null}
          >
            Add New
          </span>
          {(applicantPledges.length > 0 || newPledge) && (
            <div className="pledges-table-wrapper">
              <table className="table pledges-table">
                <colgroup>
                  <col className="pledges-col-id" />
                  <col className="pledges-col-date" />
                  <col className="pledges-col-amount" />
                  <col className="pledges-col-amount-paid" />
                  <col className="pledges-col-outstanding" />
                  <col className="pledges-col-schedule" />
                  <col className="pledges-col-actions" />
                </colgroup>
                <PledgeTableHeader />
                <tbody>
                {applicantPledges.map((ap, index) => {
                  const outstandingAmount = (ap.pledgeAmount || 0) - (ap.amountPaid || 0);

                  if (editPledgeIndex === index) {
                    const editingOutstanding =
                      this.toNumber(pledgeUpdate.pledgeAmount) - this.toNumber(ap.amountPaid);

                    return (
                      <PledgeRowEdit
                        key={ap.applicantPledgeId || index}
                        pledgeId={
                          ap.applicantPledgeId ? `#${ap.applicantPledgeId}` : NO_PLEDGE_DISPLAY
                        }
                        pledgeDate={pledgeUpdate.pledgeDate}
                        pledgeAmount={pledgeUpdate.pledgeAmount}
                        payOutSchedule={pledgeUpdate.payOutSchedule}
                        amountPaid={ap.amountPaid || 0}
                        outstandingAmount={editingOutstanding}
                        onFieldChange={this.updateFormField}
                        onSubmit={() => this.submitPledge(false)}
                        onCancel={this.resetForm}
                        disableSubmit={disableSubmit}
                        showValidationErrors={this.state.submitAttempted}
                      />
                    );
                  }

                  return (
                    <PledgeRowDisplay
                      key={ap.applicantPledgeId || index}
                      pledgeId={
                        ap.applicantPledgeId ? `#${ap.applicantPledgeId}` : NO_PLEDGE_DISPLAY
                      }
                      pledgeDate={this.displayLocalDate(ap.pledgeDate)}
                      pledgeAmount={ap.pledgeAmount}
                      amountPaid={ap.amountPaid || 0}
                      outstandingAmount={outstandingAmount}
                      payOutSchedule={ap.payOutSchedule}
                      onEdit={() => this.startEditPledge(index, ap)}
                      disableEdit={addingOrEditing}
                    />
                  );
                })}

                {newPledge && (
                  <PledgeRowEdit
                    key="new-pledge"
                    pledgeId={NO_PLEDGE_DISPLAY}
                    pledgeDate={pledgeUpdate.pledgeDate}
                    pledgeAmount={pledgeUpdate.pledgeAmount}
                    payOutSchedule={pledgeUpdate.payOutSchedule}
                    amountPaid={0}
                    outstandingAmount={this.toNumber(pledgeUpdate.pledgeAmount)}
                    onFieldChange={this.updateFormField}
                    onSubmit={() => this.submitPledge(true)}
                    onCancel={this.resetForm}
                    disableSubmit={disableSubmit}
                    showValidationErrors={this.state.submitAttempted}
                  />
                )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }
}

ApplicantPledgesTable.propTypes = {
  className: PropTypes.string,
  applicantPledges: PropTypes.arrayOf(
    PropTypes.shape({
      applicantPledgeId: PropTypes.number,
      pledgeDate: PropTypes.string,
      pledgeAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      amountPaid: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      payOutSchedule: PropTypes.string
    })
  ),
  updateRecipient: PropTypes.func.isRequired
};

ApplicantPledgesTable.defaultProps = {
  className: "",
  applicantPledges: []
};



// WEBPACK FOOTER //
// ./src/components/applicationsComponents/applicationsCommon/ApplicantPledgesTable.jsx